import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { greys, mainColors } from '../../../styling/theme';
import { hexToRGBA } from '../../../utilities/colorUtilities';
import { Section } from '../../../types/server-data/ClientConfig';
import { iconMap } from '../../../images/iconMap';
import {
  activeSectionSelector,
  allPagesSelector,
} from '../../../redux/pages/selectors';
import NavPage from './NavPage.component';
import makeStyles from '@mui/styles/makeStyles';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { EnergySavingsLeaf } from '@mui/icons-material';
import { clientNameSelector } from '../../../redux/auth/selectors';
import { useNavReconIncomingNavBadge } from '../../pages/navRecon/overview/useNavReconIncomingNavBadge';
import { pageForSection } from '../../../utilities/entityLabel';

const useStyles = makeStyles({
  removePaddingFromInput: {
    padding: '0 !important',
    color: greys.grey800,
  },
  listbox: {
    color: greys.grey800,
  },
  container: {
    width: 'fit-content',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    height: '100%',
    '& h4': {
      color: greys.grey400,
      lineHeight: 0.75,
    },

    '&:hover': {
      '& h4': {
        color: hexToRGBA(mainColors.mainBlue, 0.8),
        lineHeight: 0.75,
      },
    },
  },
  container__active: {
    '& h4': {
      color: mainColors.mainBlue,
    },
  },
  sectionTitle: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.12rem',
    padding: '0.50rem 0.75rem',
    transition: 'transform .3s',
    whiteSpace: 'nowrap',
    '&:hover': {
      transform: 'scale(1.2)',
    },
  },
  sectionTitle__active: {
    transform: 'scale(1.2)',
  },
  navPagesContainer: {
    padding: '1.50rem 0 3.00rem',
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    overflowY: 'auto',
  },
  navPagesTitle: {
    display: 'flex',
    alignItems: 'flex-end',
    '& h2': {
      fontSize: '2.00rem',
      lineHeight: 0.95,
      letterSpacing: 2,
      color: mainColors.mainBlue,
    },
  },
  pageAreasContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: '2.50rem',
    gridColumnGap: '2.50rem',
    justifyContent: 'start',
    gridTemplateRows: 'repeat(1, 1fr)',
  },
  pagesAndTitle: {
    width: '95%',
    margin: '0 auto',
  },

  pageArea: {
    '& h1': {
      marginTop: 0,
      marginBottom: '0.50rem',
      fontSize: '1.50rem',
      color: '#DBA11C',
      letterSpacing: 1,
      fontWeight: 400,
      paddingBottom: '0.62rem',
      borderBottom: '2px solid #DBA11C',
    },
  },
  pageAreaPages: {
    display: 'grid',
    gridTemplateRows: 'repeat(5, min-content)',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    '& a': {
      marginRight: '1.25rem',
      justifySelf: 'start',
    },
  },
  link: {
    fontSize: '1.12rem',
    textDecoration: 'none',
    color: 'inherit',
    '& h2': {
      textTransform: 'uppercase',
      marginRight: '0.62rem',
      fontSize: '1.25rem',
      fontWeight: 700,
    },
  },
});
const pageAreaMap: any = {
  Reports: 5,
  Liquidity: 3,
  ['Asset Class Data']: 2,
  Utilities: 1,
};

interface NavSectionProps {
  section: Section;
}

// This is a very awful hack for Peter, he wants HVB to show DFM instead of RIAIF in a bunch of places.
// This needs to be removed after their demo.
// I have created a reusable function, please search for it's use everywhere and remove after demo

// make a fucntion for checking clientName and schema name and returning the hack
export const hvbDfmHack = (clientName: string | null, schema: string) => {
  if (clientName === 'hvb' && schema === 'RIAIF') {
    return 'DFM';
  }
  return schema;
};

const NavSection: React.FC<NavSectionProps> = ({ section }) => {
  const navigate = useNavigate();
  const clientName = useSelector(clientNameSelector);
  const classes = useStyles();
  const allPages = useSelector(allPagesSelector);
  const showTimeout = useRef<NodeJS.Timeout | null>(null);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);

  const activeSection = useSelector(activeSectionSelector);

  const [menuShowing, setMenuShowing] = useState<boolean>(false);
  const isSectionActive = activeSection === section.sectionId;

  const isNavReconSection = section.sectionId === 'nav-recon';
  const hasIncomingNavs = useNavReconIncomingNavBadge(isNavReconSection);
  const showIncomingNavBadge = hasIncomingNavs && !isSectionActive;

  const handleMouseEnter = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    showTimeout.current = setTimeout(() => setMenuShowing(true), 100);
  };

  const handleMouseLeave = () => {
    if (showTimeout.current) clearTimeout(showTimeout.current);
    hideTimeout.current = setTimeout(() => setMenuShowing(false), 150);
  };

  const pageAreas: string[] = [];
  section.pages.forEach((page: string) => {
    if (!allPages) return;
    const pageDetails = allPages[page];
    if (!pageAreas.includes(pageDetails.pageArea)) {
      pageAreas.push(pageDetails.pageArea);
    }
  });

  return (
    <div
      className={
        isSectionActive
          ? clsx(classes.container, classes.container__active)
          : classes.container
      }
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link className={classes.link} to={'/' + section.sectionId}>
        <Typography
          variant="h4"
          className={
            isSectionActive
              ? clsx(classes.sectionTitle, classes.sectionTitle__active)
              : classes.sectionTitle
          }
        >
          {hvbDfmHack(clientName, section.sectionTitle)}
          {/* Add section icons here... */}
          {section.sectionTitle === 'ESG' ? (
            <EnergySavingsLeaf
              style={{
                fill: mainColors.Pass,
                fontSize: '1.12rem',
                marginBottom: '0.12rem',
              }}
            />
          ) : null}
          {showIncomingNavBadge ? (
            <span
              className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500"
              title="First nav of the date received"
              aria-label="First nav of the date received"
            />
          ) : null}
        </Typography>
      </Link>
      {menuShowing && section.sectionId !== 'dashboard' && (
        <div className="fixed left-0 top-[2.81rem] z-[-1] w-screen border-b border-gray-300 bg-black bg-transparent shadow-[0_3px_3px_rgba(0,0,0,0.15)] transition-opacity">
          <div className={classes.navPagesContainer}>
            <div className={classes.pagesAndTitle}>
              <div className={classes.navPagesTitle}>
                <h2 className="mr-2 font-semibold">{section.sectionTitle}</h2>
                {allPages && (
                  <Autocomplete
                    id="page-select-box"
                    options={section.pages.map((page: string) =>
                      pageForSection(allPages[page], section.sectionId),
                    )}
                    getOptionLabel={(option) => option.pageTitle}
                    style={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          disableUnderline: true,
                          classes: {
                            input: classes.removePaddingFromInput,
                          },
                          ...params.InputProps,
                        }}
                        variant="standard"
                        placeholder="Select a page"
                        autoFocus={true}
                      />
                    )}
                    classes={{ listbox: classes.listbox }}
                    onChange={(event: any, value: any) => {
                      navigate(`/${section.sectionId}/${value.pageKey}`);
                      setMenuShowing(false);
                    }}
                  />
                )}
              </div>

              <div className={classes.pageAreasContainer}>
                {allPages &&
                  pageAreas
                    .sort(
                      (a: string, b: string) => pageAreaMap[b] - pageAreaMap[a],
                    )
                    .map((pageArea: string) => {
                      return (
                        <div
                          key={section.sectionId + pageArea}
                          className={classes.pageArea}
                        >
                          <h1>{pageArea}</h1>
                          <div className={classes.pageAreaPages}>
                            {(() => {
                              const pagesToRender = section.pages.map(
                                (pageId: string) => {
                                  const page = pageForSection(
                                    allPages[pageId],
                                    section.sectionId,
                                  );
                                  const Icon = iconMap(page.iconKey);
                                  if (page.pageArea === pageArea) {
                                    return { page, icon: Icon };
                                  } else {
                                    return null;
                                  }
                                },
                              );

                              return pagesToRender
                                .filter(
                                  (pageDetails: any) =>
                                    pageDetails !== null &&
                                    pageDetails.page.page_is_active,
                                )
                                .map((pageDetails: any) => {
                                  const { page, icon } = pageDetails;
                                  return (
                                    <NavPage
                                      Icon={icon}
                                      section={section}
                                      page={page}
                                      key={page.pageKey}
                                      handleShowing={() =>
                                        setMenuShowing(false)
                                      }
                                    />
                                  );
                                });
                            })()}
                          </div>
                        </div>
                      );
                    })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavSection;
